// WhatsApp supervisors (doc 14): the questions TwinLink asks the supervisors of a well, their
// answers, and what SmarDac Messenger posts to TwinLink.
import { z } from 'zod';
import { objectIdSchema } from './common';

const isoDate = z.string().datetime({ offset: true });

// ---- Supervisors of a well ----
export const MAX_SUPERVISORS = 10;
/** Sudden changes (doc 14 §3.1): any numeric signal of the well moving by `percent` within `windowMin`, unexplained. */
export const jumpWatchSchema = z.object({
  enabled: z.boolean(),
  percent: z.number().min(1).max(1000),
  windowMin: z.number().int().min(1).max(60),
});
export type JumpWatch = z.infer<typeof jumpWatchSchema>;
export const DEFAULT_JUMP_WATCH: JumpWatch = { enabled: false, percent: 20, windowMin: 2 };

export const wellSupervisorsRequestSchema = z.object({ userIds: z.array(objectIdSchema).max(MAX_SUPERVISORS), jumpWatch: jumpWatchSchema.optional() });
export type WellSupervisorsRequest = z.infer<typeof wellSupervisorsRequestSchema>;

export const supervisorSchema = z.object({
  userId: objectIdSchema,
  name: z.string(),
  phone: z.string().nullable(),
  /** Can be asked now: has a phone, an active membership, access to the well, and is not a Client. */
  reachable: z.boolean(),
});
export type Supervisor = z.infer<typeof supervisorSchema>;
export const wellSupervisorsResponseSchema = z.object({
  items: z.array(supervisorSchema),
  /** Members who could be chosen (only for those who may change the list). */
  candidates: z.array(supervisorSchema),
  jumpWatch: jumpWatchSchema,
});
export type WellSupervisorsResponse = z.infer<typeof wellSupervisorsResponseSchema>;

// ---- Inquiries ----
export const INQUIRY_STATUSES = ['open', 'answered', 'unanswered', 'cancelled'] as const;
export type InquiryStatus = (typeof INQUIRY_STATUSES)[number];
export const DELIVERY_STATUSES = ['queued', 'sent', 'delivered', 'read', 'failed', 'skipped'] as const;

export const inquirySchema = z.object({
  id: objectIdSchema,
  fieldId: objectIdSchema,
  wellId: objectIdSchema,
  testId: objectIdSchema,
  /** The moment asked about. */
  at: z.string(),
  source: z.enum(['alert', 'manual', 'jump']),
  /** What it is about: the alert name, or the question an engineer typed. */
  title: z.string(),
  question: z.string(),
  status: z.enum(INQUIRY_STATUSES),
  asked: z.array(z.object({ userId: objectIdSchema, name: z.string(), status: z.enum(DELIVERY_STATUSES), error: z.string().nullable() })),
  answers: z.array(
    z.object({
      userId: objectIdSchema,
      name: z.string(),
      text: z.string(),
      originalText: z.string(),
      receivedAt: z.string(),
      /** The answer that went into the event (the first one). */
      used: z.boolean(),
    }),
  ),
  reminders: z.number(),
  seqLogEntryId: objectIdSchema.nullable(),
  askedByName: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
});
export type Inquiry = z.infer<typeof inquirySchema>;

export const inquiryListQuerySchema = z.object({
  status: z.enum(INQUIRY_STATUSES).optional(),
  wellId: objectIdSchema.optional(),
  testId: objectIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

/**
 * "Ask the supervisors" about a moment (doc 14 §3): of a test, or of a well — then the test that
 * covers that moment (from a right-click on a chart).
 */
export const askSupervisorsRequestSchema = z
  .object({
    testId: objectIdSchema.optional(),
    wellId: objectIdSchema.optional(),
    at: isoDate,
    question: z.string().trim().min(3).max(500),
  })
  .refine((v) => !!v.testId !== !!v.wellId, { message: 'Give a test or a well', path: ['testId'] });
export type AskSupervisorsRequest = z.infer<typeof askSupervisorsRequestSchema>;

// ---- From SmarDac Messenger (signed; doc 14 §8.2) ----
export const messengerInboundSchema = z.object({
  id: z.string().min(1).max(100),
  messageId: z.string().min(1).max(100),
  from: z.string().min(8).max(20),
  text: z.string().max(4096),
  type: z.string().max(32).default('text'),
  receivedAt: z.string(),
  ref: z.string().max(100).optional(),
  quotedMessageId: z.string().max(100).optional(),
});
export type MessengerInbound = z.infer<typeof messengerInboundSchema>;

export const messengerReplySchema = z.object({
  reply: z.object({ title: z.string().optional(), text: z.string(), lang: z.enum(['en', 'fr', 'ar']).optional() }).optional(),
});
export type MessengerReply = z.infer<typeof messengerReplySchema>;

export const messengerStatusSchema = z.object({
  id: z.string().min(1).max(200),
  messageId: z.string().min(1).max(100),
  clientId: z.string().max(100).nullable().optional(),
  status: z.enum(['sent', 'delivered', 'read', 'failed', 'skipped']),
  error: z.string().max(500).optional(),
  at: z.string().optional(),
});
export type MessengerStatus = z.infer<typeof messengerStatusSchema>;
